import { apiClient } from '@/api/client'
import {
  companyAttendanceTodayPath,
  companyAttendanceClockInPath,
  companyAttendanceBreakStartPath,
  companyAttendanceBreakEndPath,
  companyAttendanceClockOutPath,
  companyAttendanceEndDayPath,
  companyAttendanceByIdPath,
  ATTENDANCE,
  ATTENDANCE_ME,
  ATTENDANCE_ME_TODAY,
} from '@/api/endpoints'
import type { ApiResponseWrapper } from '@/types/api.types'
import type {
  Attendance,
  AttendanceToday,
  MyAttendanceFilterQueryDto,
  AttendanceTodayResponse,
  AttendanceListResponse,
  AttendanceDetailResponse,
  ClockInDto,
  BreakStartDto,
  ClockInResponse,
  BreakStartResponse,
  BreakEndResponse,
  ClockOutResponse,
  EndDayResponse,
  MyAttendanceTodayResponse,
  MyAttendanceTodayCompanyEntry,
} from '@/features/attendance/types/attendance.types'

interface ListPayload {
  data: Attendance[]
  pagination: { total: number; page: number; limit: number }
  serverTime?: string
}

function toListResponse(payload: ListPayload): AttendanceListResponse {
  return {
    data: Array.isArray(payload.data) ? payload.data : [],
    pagination: payload.pagination ?? { total: 0, page: 1, limit: 10 },
    serverTime: payload.serverTime,
  }
}

export async function getAttendanceToday(companyId: string): Promise<AttendanceTodayResponse> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{ data: AttendanceToday | null; serverTime?: string }>
  >(companyAttendanceTodayPath(companyId))
  const inner = data?.data
  return {
    data: inner?.data ?? null,
    serverTime: inner?.serverTime,
  }
}

export async function clockIn(
  companyId: string,
  payload: ClockInDto
): Promise<{ data: ClockInResponse; serverTime?: string }> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ data: ClockInResponse; serverTime?: string }>
  >(companyAttendanceClockInPath(companyId), payload)
  const inner = data?.data
  if (!inner?.data) throw new Error('Invalid clock-in response')
  return { data: inner.data, serverTime: inner.serverTime }
}

export async function startBreak(
  companyId: string,
  payload?: BreakStartDto
): Promise<{ data: BreakStartResponse; serverTime?: string }> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ data: BreakStartResponse; serverTime?: string }>
  >(companyAttendanceBreakStartPath(companyId), payload ?? {})
  const inner = data?.data
  if (!inner?.data) throw new Error('Invalid break start response')
  return { data: inner.data, serverTime: inner.serverTime }
}

export async function endBreak(companyId: string): Promise<{
  data: BreakEndResponse
  serverTime?: string
}> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ data: BreakEndResponse; serverTime?: string }>
  >(companyAttendanceBreakEndPath(companyId))
  const inner = data?.data
  if (!inner?.data) throw new Error('Invalid break end response')
  return { data: inner.data, serverTime: inner.serverTime }
}

export async function clockOut(companyId: string): Promise<{
  data: ClockOutResponse
  serverTime?: string
}> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ data: ClockOutResponse; serverTime?: string }>
  >(companyAttendanceClockOutPath(companyId))
  const inner = data?.data
  if (!inner?.data) throw new Error('Invalid clock-out response')
  return { data: inner.data, serverTime: inner.serverTime }
}

export async function endDay(companyId: string): Promise<{
  data: EndDayResponse
  serverTime?: string
}> {
  const { data } = await apiClient.post<
    ApiResponseWrapper<{ data: EndDayResponse; serverTime?: string }>
  >(companyAttendanceEndDayPath(companyId))
  const inner = data?.data
  if (!inner?.data) throw new Error('Invalid end-day response')
  return { data: inner.data, serverTime: inner.serverTime }
}

/** Cross-company today status: one entry per company the caller belongs to. */
export async function getMyAttendanceToday(): Promise<MyAttendanceTodayResponse> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{
      data: MyAttendanceTodayCompanyEntry[]
      serverTime?: string
    }>
  >(ATTENDANCE_ME_TODAY)
  const inner = data?.data
  return {
    data: Array.isArray(inner?.data) ? inner!.data : [],
    serverTime: inner?.serverTime,
  }
}

/** Cross-company self-service attendance: backend auto-scopes to caller's memberships. */
export async function getMyAttendanceAcrossCompanies(
  query?: MyAttendanceFilterQueryDto
): Promise<AttendanceListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<ListPayload>>(
    ATTENDANCE_ME,
    { params }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toListResponse(payload)
  }
  return toListResponse({
    data: [],
    pagination: { total: 0, page: 1, limit: query?.limit ?? 10 },
  })
}

/** Operator workspace: cross-company attendance. Backend intersects requested companyIds with caller's memberships. */
export async function getAttendanceWorkspace(
  query?: MyAttendanceFilterQueryDto
): Promise<AttendanceListResponse> {
  const { companyIds, ...rest } = query ?? {}
  const params: Record<string, unknown> = { ...rest }
  if (companyIds && companyIds.length > 0) {
    params.companyIds = companyIds.join(',')
  }
  const { data } = await apiClient.get<ApiResponseWrapper<ListPayload>>(
    ATTENDANCE,
    { params }
  )
  const payload = data?.data
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return toListResponse(payload)
  }
  return toListResponse({
    data: [],
    pagination: { total: 0, page: 1, limit: query?.limit ?? 10 },
  })
}

export interface UpdateAttendanceBody {
  clockIn?: string
  clockOut?: string
  note?: string
}

/** Direct edit by a manager/HR/admin with `attendance:update`. */
export async function updateAttendance(
  companyId: string,
  id: string,
  body: UpdateAttendanceBody
): Promise<Attendance> {
  const { data } = await apiClient.patch<ApiResponseWrapper<Attendance>>(
    companyAttendanceByIdPath(companyId, id),
    body
  )
  return data.data
}

export async function getAttendanceById(
  companyId: string,
  id: string
): Promise<AttendanceDetailResponse> {
  const { data } = await apiClient.get<
    ApiResponseWrapper<{ data: Attendance; serverTime?: string }>
  >(companyAttendanceByIdPath(companyId, id))
  const inner = data?.data
  if (!inner?.data) throw new Error('Attendance not found')
  return { data: inner.data, serverTime: inner.serverTime }
}
